// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { useTheme } from '@mui/system';
import { Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, Slide, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import React, { useEffect, useState } from 'react';
import { TransitionProps } from '@mui/material/transitions';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { getAllFoldersThunk, moveProjectToFolderThunk } from 'Features/folderList/folderListThunk';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import stylesDef from './styles';

interface IMoveProjectToFolderDialogProps {
  showDialog: boolean;
  projectId: string;
  projectFolderId: string;
  setShowDialog: (show: boolean) => void;
  onSuccess?: (project?: IProject, folderId?: string) => void;
}

const Transition = React.forwardRef<unknown, TransitionProps>((props, ref) => <Slide ref={ref} direction="up" {...props} />);

const MoveProjectToFolderDialog = ({ showDialog, projectId, projectFolderId, setShowDialog, onSuccess }: IMoveProjectToFolderDialogProps) => {
  // Constants
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();

  // State Variables
  const { folderList } = useAppSelector((state) => state.folderList);
  const [loading, setLoading] = useState(false);
  const [getAllFoldersLoading, setGetAllFoldersLoading] = useState(false);

  // useEffect
  useEffect(() => {
    if (showDialog && folderList.length === 0) {
      getAllFolders();
    }
  }, [showDialog]);

  // Methods
  const getAllFolders = async () => {
    setGetAllFoldersLoading(true);
    // API call to get all folders
    const response = await dispatch(getAllFoldersThunk());

    if (response) {
      setGetAllFoldersLoading(false);
    }
  };

  const handleMoveProjectToFolder = async (folderId: string) => {
    // Validate folderId and projectId
    if (!folderId || !projectId) {
      return;
    }

    setLoading(true);
    // API call to move the project to the selected folder using folderId and projectId
    const response = await dispatch(moveProjectToFolderThunk(projectId, folderId));
    setLoading(false);
    if (response.success) {
      setShowDialog(false);
      onSuccess?.(response.data as IProject, folderId);
    }
  };

  return (
    <Dialog
      open={showDialog}
      onClose={() => setShowDialog(false)}
      TransitionComponent={Transition}
      keepMounted={false}
      sx={styles.dialog}
    >
      <Box sx={styles.titleSection}>
        <DialogTitle sx={styles.dialogTitle}>
          Move To...
        </DialogTitle>
        <IconButton
          color="inherit"
          onClick={() => setShowDialog(false)}
          aria-label="Close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <DialogContent sx={styles.dialogContent}>
        <Typography variant="body1" sx={styles.dialogText}>
          Please select the folder where you want to move this project to:
        </Typography>
        <Box sx={styles.folderListContainer}>
          {/* Get all folder loading */}
          { getAllFoldersLoading && (
            <Box sx={styles.loadingContainer}>
              <CircularProgress size={50} />
            </Box>
          )}

          {/* Folders list */}
          {!getAllFoldersLoading && folderList?.length > 0 && folderList.map((folder) => (
            <Box key={folder._id} sx={styles.folderItem}>
              <Box sx={styles.folderDetails}>
                <FolderOutlinedIcon sx={styles.folderIcon} />
                <Typography variant="h6" sx={styles.folderName}>{folder.name}</Typography>
              </Box>
              <Button
                variant="text"
                color="primary"
                className="moveButton"
                disabled={loading || folder._id === projectFolderId}
                onClick={() => handleMoveProjectToFolder(folder._id)}
              >
                {loading ? (
                  <CircularProgress size={20} sx={styles.buttonProgress} />
                ) : 'Move'}
              </Button>
            </Box>
          ))}

          {/* No folder created */}
          {!getAllFoldersLoading && folderList?.length === 0 && (
            <Box sx={styles.noFoldersContainer}>
              <Box sx={styles.noFoldersIconContainer}>
                <FolderOutlinedIcon />
              </Box>
              <Typography variant="h6" sx={styles.noFoldersText}>
                No folders created yet.
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default MoveProjectToFolderDialog;
